import { ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

import { useNavigateToLink } from "../hooks/useNavigateToLink";
import { ROUTES } from "../routes";

import { useReducedMotion } from "./ui/use-reduced-motion";

// Real product recording: agent generates the "Cirrus" flight app on the
// canvas, then a manual edit through the property panel.
const HERO_VIDEO = "/videos/hero-section.mp4";

const HEADLINE_LINES = ["The design canvas", "for agents."];
const HEADLINE_TEXT = HEADLINE_LINES.join(" ");
const TOTAL_CHARS = HEADLINE_LINES.reduce((sum, line) => sum + line.length, 0);

const TYPE_START_MS = 700;
const MS_PER_CHAR = 45;

export default function LandingHero() {
  const prefersReducedMotion = useReducedMotion();
  const navigateToLink = useNavigateToLink();
  const [typedChars, setTypedChars] = useState(0);
  const [caretVisible, setCaretVisible] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setTypedChars(TOTAL_CHARS);
      setCaretVisible(false);
      return;
    }

    const timers: number[] = [];
    const at = (ms: number, fn: () => void) =>
      timers.push(window.setTimeout(fn, ms));

    // The caret grows in first, then the headline types out beneath it.
    at(300, () => setCaretVisible(true));
    for (let i = 1; i <= TOTAL_CHARS; i++) {
      at(TYPE_START_MS + i * MS_PER_CHAR, () => setTypedChars(i));
    }
    const doneMs = TYPE_START_MS + TOTAL_CHARS * MS_PER_CHAR;
    at(doneMs + 350, () => setCaretVisible(false));

    return () => timers.forEach(clearTimeout);
  }, [prefersReducedMotion]);

  // Which line the caret currently sits on.
  let caretLine = HEADLINE_LINES.length - 1;
  let lineStart = 0;
  for (let i = 0; i < HEADLINE_LINES.length; i++) {
    if (typedChars < lineStart + HEADLINE_LINES[i].length) {
      caretLine = i;
      break;
    }
    lineStart += HEADLINE_LINES[i].length;
  }

  return (
    <section className="relative w-full px-4 pt-32 pb-16 md:px-8 lg:px-16">
      <div className="mx-auto w-full max-w-[1600px]">
        {/* Centered headline + CTA filling the first viewport; the video's
            top edge peeks in below the fold. */}
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0.2, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.8, ease: "easeOut" }
          }
          className="flex min-h-[calc(100svh-22rem)] flex-col items-center justify-center text-center"
        >
          <div className="relative inline-block">
            <h1 className="text-[2.75rem] leading-[1.05] font-semibold tracking-tight text-gray-900 lg:text-[clamp(3.5rem,7.2vw,7rem)] dark:text-white">
              {/* Full headline for SEO and screen readers; the typewriter
                    below is presentational. */}
              <span className="sr-only">{HEADLINE_TEXT}</span>
              {HEADLINE_LINES.map((line, i) => {
                const start = HEADLINE_LINES.slice(0, i).reduce(
                  (sum, l) => sum + l.length,
                  0,
                );
                const typed = line.slice(
                  0,
                  Math.max(0, Math.min(line.length, typedChars - start)),
                );
                return (
                  <span key={line} aria-hidden className="relative block">
                    {/* Invisible full line reserves the layout so nothing
                          shifts while typing. */}
                    <span className="invisible">{line}</span>
                    <span className="absolute inset-0">
                      {typed}
                      <AnimatePresence>
                        {caretVisible && caretLine === i && (
                          /* Zero-width anchor so the caret never adds layout
                             width — a full line + caret must not wrap. */
                          <span className="relative inline-block w-0">
                            <motion.span
                              initial={{ opacity: 0, scaleY: 0.2 }}
                              animate={{ opacity: 1, scaleY: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.25, ease: "easeOut" }}
                              className="absolute bottom-[0.08em] left-[0.06em] w-[0.14em] origin-bottom rounded-[2px] bg-[#0247FE]"
                              style={{ height: "0.85em" }}
                            />
                          </span>
                        )}
                      </AnimatePresence>
                    </span>
                  </span>
                );
              })}
            </h1>
          </div>

          <div className="mt-10 flex flex-col items-center gap-3">
            <button
              onClick={(e) => navigateToLink(e, ROUTES.register)}
              className="group inline-flex cursor-pointer items-center gap-3 rounded-full px-8 py-4 text-white shadow-lg transition-all hover:shadow-xl"
              style={{ backgroundColor: "#0247FE" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#0139D1")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#0247FE")
              }
            >
              <span className="text-lg">Try for free</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No credit card required
            </p>
          </div>
        </motion.div>

        {/* Huge framed product demo video — top edge visible above the fold,
            scroll to take it in fully. */}
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0.2, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={
            prefersReducedMotion ? { duration: 0 } : { duration: 0.8 }
          }
          className="mt-10 w-full"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_0_80px_rgba(2,71,254,0.2)]">
            <video
              src={HERO_VIDEO}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label="An agent designing a flight-booking app on the Flowstep canvas"
              className="aspect-video w-full object-cover"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
